import { useState, useEffect } from 'react';
import { parseAssignedGradeSplitIds, parseAssignedGrades } from '@car-pool/shared/schoolRecords';
import DashboardHeader from '../components/DashboardHeader';
import { apiFetch } from '../config/api';
import { getFallbackGradeLabel } from '../lib/gradeLabels';
import styles from './GroupSelect.module.css';

function normalizeGrades(items = [], key = 'grade_code') {
  const seen = new Set();

  return items
    .map((grade) => {
      const code = Number(grade[key]);
      if (Number.isNaN(code)) return null;

      return {
        ...grade,
        grade_code: code,
        label: grade.label || getFallbackGradeLabel(code),
      };
    })
    .filter(Boolean)
    .filter((grade) => {
      if (seen.has(grade.grade_code)) return false;
      seen.add(grade.grade_code);
      return true;
    })
    .sort((a, b) => a.grade_code - b.grade_code);
}

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

const GROUPING_MODES = new Set(['grades', 'grades_and_splits', 'splits_only']);

function normalizeGroupingMode(value) {
  return GROUPING_MODES.has(value) ? value : 'grades';
}

function gradeKey(gradeCode) {
  return `grade:${Number(gradeCode)}`;
}

function splitKey(splitId) {
  return `split:${Number(splitId)}`;
}

function splitCodeGroupKey(splitCode) {
  return `split-code:${String(splitCode || '').trim().toLowerCase()}`;
}

function buildSplitOnlyOptions(
  grades = [],
  splits = [],
  assignedGradeSet = new Set(),
  assignedSplitSet = new Set(),
  restricted = false
) {
  const useAssignedSplits = assignedSplitSet.size > 0;
  const availableGrades = new Set(
    grades
      .map((grade) => Number(grade.grade_code))
      .filter((gradeCode) => Number.isInteger(gradeCode))
  );
  const groupsByCode = new Map();

  splits
    .filter((split) => isActive(split.is_active))
    .forEach((split) => {
      const gradeCode = Number(split.grade);
      const splitId = Number(split.id);
      const splitCode = String(split.split_code || '').trim();
      if (!Number.isInteger(gradeCode) || !Number.isInteger(splitId) || !splitCode) return;
      if (availableGrades.size > 0 && !availableGrades.has(gradeCode)) return;
      if (restricted && useAssignedSplits && !assignedSplitSet.has(splitId)) return;
      if (restricted && !useAssignedSplits && !assignedGradeSet.has(gradeCode)) return;

      const key = splitCodeGroupKey(splitCode);
      if (!groupsByCode.has(key)) {
        groupsByCode.set(key, {
          key,
          type: 'split_code',
          split_code: splitCode,
          label: splitCode,
          grade_split_ids: [],
          fallback_grades: [],
        });
      }

      const group = groupsByCode.get(key);
      group.grade_split_ids.push(splitId);
      if (!useAssignedSplits && !group.fallback_grades.includes(gradeCode)) {
        group.fallback_grades.push(gradeCode);
      }
    });

  return [...groupsByCode.values()]
    .map((group) => ({
      ...group,
      grade_split_ids: [...new Set(group.grade_split_ids)].sort((a, b) => a - b),
      fallback_grades: [...new Set(group.fallback_grades)].sort((a, b) => a - b),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function buildGroupOptions(
  grades = [],
  splits = [],
  assignedGrades = [],
  assignedGradeSplitIds = [],
  groupingMode = 'grades'
) {
  const mode = normalizeGroupingMode(groupingMode);
  const assignedGradeSet = new Set(assignedGrades.map(Number));
  const assignedSplitSet = new Set(assignedGradeSplitIds.map(Number));
  const restricted = assignedGradeSet.size > 0 || assignedSplitSet.size > 0;
  const gradesCoveredByAssignedSplits = new Set(
    splits
      .filter((split) => assignedSplitSet.has(Number(split.id)))
      .map((split) => Number(split.grade))
  );
  const gradeLabelByCode = new Map(grades.map((grade) => [Number(grade.grade_code), grade.label]));
  const options = [];

  if (mode === 'splits_only') {
    return buildSplitOnlyOptions(grades, splits, assignedGradeSet, assignedSplitSet, restricted);
  }

  grades.forEach((grade) => {
    const gradeCode = Number(grade.grade_code);
    if (!Number.isInteger(gradeCode)) return;
    const gradeAssigned = assignedGradeSet.has(gradeCode);
    if (mode === 'grades' && restricted && !gradeAssigned && !gradesCoveredByAssignedSplits.has(gradeCode)) return;
    const mayShowWholeGrade = mode !== 'grades_and_splits' || !restricted || gradeAssigned;
    if (mayShowWholeGrade) {
      options.push({
        key: gradeKey(gradeCode),
        type: 'grade',
        grade: gradeCode,
        label: grade.label || getFallbackGradeLabel(gradeCode),
      });
    }

    if (mode !== 'grades_and_splits') return;

    splits
      .filter((split) => Number(split.grade) === gradeCode && isActive(split.is_active))
      .sort((left, right) => String(left.split_code || '').localeCompare(String(right.split_code || '')))
      .forEach((split) => {
        const splitId = Number(split.id);
        if (restricted && !gradeAssigned && !assignedSplitSet.has(splitId)) return;
        options.push({
          key: splitKey(splitId),
          type: 'split',
          grade: gradeCode,
          grade_split_id: splitId,
          label: `${gradeLabelByCode.get(gradeCode) || getFallbackGradeLabel(gradeCode)} - ${split.split_code}`,
        });
      });
  });

  return options;
}

export default function GroupSelect({ user, onLogout, onViewBoard }) {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    let active = true;

    const loadGrades = async () => {
      setLoading(true);
      setStatusMessage('');

      const schoolId = user.school_id ?? user.school_ids?.[0];
      const assigned = parseAssignedGrades(user.assigned_grades);
      const assignedGradeSplitIds = parseAssignedGradeSplitIds(user.assigned_grade_split_ids);
      const restricted = assigned.length > 0 || assignedGradeSplitIds.length > 0;
      let groupingMode = 'grades';

      if (!schoolId) {
        if (!active) return;
        const fallbackGrades = normalizeGrades(assigned.map((gradeCode) => ({ grade_code: gradeCode })));
        const options = buildGroupOptions(fallbackGrades, [], assigned, assignedGradeSplitIds, groupingMode);
        setGroups(options);
        setSelected(new Set(restricted ? options.map((option) => option.key) : []));
        setStatusMessage('No school is assigned to this account.');
        setLoading(false);
        return;
      }

      try {
        const [schoolRes, gradeNamesRes, gradeSplitsRes] = await Promise.all([
          apiFetch(`/schools/${schoolId}`, {}, user.token).catch(() => ({ data: null })),
          apiFetch(`/school-grade-names?school_id=${schoolId}&is_active=1`, {}, user.token),
          apiFetch(`/school-grade-splits?school_id=${schoolId}&is_active=true`, {}, user.token).catch(() => ({ data: [] })),
        ]);
        groupingMode = normalizeGroupingMode(schoolRes.data?.student_grouping_mode);
        const activeSplits = (gradeSplitsRes.data || []).filter((split) => isActive(split.is_active));
        const configuredGrades = normalizeGrades(
          (gradeNamesRes.data || []).filter((g) => isActive(g.is_active ?? 1))
        );
        const splitGrades = normalizeGrades(activeSplits.map((split) => ({ grade_code: split.grade })));
        const availableGrades = configuredGrades.length > 0 ? configuredGrades : splitGrades;

        if (availableGrades.length > 0 || activeSplits.length > 0) {
          if (!active) return;
          const options = buildGroupOptions(
            availableGrades,
            activeSplits,
            assigned,
            assignedGradeSplitIds,
            groupingMode
          );
          setGroups(options);
          setSelected(new Set(restricted ? options.map((option) => option.key) : []));
          if (options.length === 0) {
            setStatusMessage(groupingMode === 'splits_only'
              ? 'No assigned grade splits found for this account.'
              : 'No assigned grades found for this account.');
          }
          setLoading(false);
          return;
        }
      } catch {
        // Fall through to assigned grades / student distribution.
      }

      if (assigned.length > 0) {
        if (!active) return;
        const fallbackGrades = normalizeGrades(assigned.map((gradeCode) => ({ grade_code: gradeCode })));
        const options = buildGroupOptions(fallbackGrades, [], assigned, assignedGradeSplitIds, groupingMode);
        setGroups(options);
        setSelected(new Set(restricted ? options.map((option) => option.key) : []));
        setLoading(false);
        return;
      }

      try {
        const distributionRes = await apiFetch(`/students/grade-distribution/${schoolId}`, {}, user.token);
        const distributionGrades = normalizeGrades(
          (distributionRes.data || []).map((item) => ({
            grade: item.grade,
            grade_code: item.grade,
            label: getFallbackGradeLabel(item.grade),
          }))
        );

        if (!active) return;

        const options = buildGroupOptions(distributionGrades, [], assigned, assignedGradeSplitIds, groupingMode);
        setGroups(options);
        setSelected(new Set(restricted ? options.map((option) => option.key) : []));
        if (distributionGrades.length === 0) {
          setStatusMessage('No grades found for this school.');
        }
      } catch {
        if (!active) return;
        setGroups([]);
        setStatusMessage('Could not load grades for this school.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadGrades();

    return () => {
      active = false;
    };
  }, [user.assigned_grade_split_ids, user.assigned_grades, user.school_id, user.school_ids, user.token]);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const selectedGroups = groups.filter((group) => selected.has(group.key));

  const handleView = () => {
    if (selectedGroups.length === 0) return;
    onViewBoard(selectedGroups);
  };

  return (
    <div className={styles.page}>
      <DashboardHeader user={user} onLogout={onLogout} />

      <div className={styles.body}>
        <div className={`glass-card ${styles.card}`}>
          <div className={styles.left}>
            <h2 className={styles.title}>Select Groups<br />to Monitor</h2>
            <p className={styles.desc}>
              Choose one or more groups to display on the live dismissal board.
            </p>
          </div>

          <div className={styles.right}>
            {loading ? (
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading grades…</p>
            ) : groups.length === 0 ? (
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>{statusMessage || 'No grades found for this school.'}</p>
            ) : (
              <div className={styles.grid}>
                {groups.map((g) => (
                  <button
                    key={g.key}
                    className={`${styles.gradeBtn} ${selected.has(g.key) ? styles.gradeBtnActive : ''}`}
                    onClick={() => toggle(g.key)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            )}

            <button
              className={styles.viewBtn}
              onClick={handleView}
              disabled={selectedGroups.length === 0}
            >
              View Live Dismissal Board
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
